import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SubmitButton } from "@/components/ui/submit-button";
import VehicleSelect from "@/components/ui/vehicle-select";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { errorHandler } from "@/lib/utils";
import { tagService } from "@/services/api/tags";
import { zodResolver } from "@hookform/resolvers/zod";
import { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import { AuthContext } from "../../../contexts/auth/AuthContext";

const formSchema = z.object({
  name: z.literal("grupo"),
  value: z.string().min(1, "O nome do grupo é obrigatório"),
  vehicles: z.array(z.string()).optional(),
  client_id: z.number().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const VehicleGroupsForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const userId = authContext?.userId;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "grupo",
      value: "",
      vehicles: [],
      client_id: userId ?? undefined,
    },
  });

  useEffect(() => {
    if (id) {
      const loadTag = async () => {
        try {
          const response = await tagService.show(Number(id));
          form.reset({
            name: "grupo",
            value: response.data.value,
            vehicles:
              response.data.vehicles?.map((vehicle) => vehicle.id.toString()) ||
              [],
            client_id: userId ?? 0,
          });
        } catch (err) {
          errorHandler(err, "Erro ao carregar grupo");
          navigate("/groups");
        }
      };

      loadTag();
    }
  }, [id, form, navigate, userId]);

  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: tagService.store,
    updateService: tagService.update,
    onSuccess: () => navigate("/groups"),
  });

  const onSubmit = async (values: FormValues) => {
    const processedValues = {
      ...values,
      client_id: userId ?? 0,
      vehicles: values.vehicles?.map((id) => Number(id)),
    };

    await handleSubmit(processedValues);
  };

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>{id ? "Editar Grupo" : "Novo Grupo"}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="vehicles"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Veículos</FormLabel>
                  <VehicleSelect
                    value={field.value
                      ?.map((id) => (!isNaN(Number(id)) ? Number(id) : 0))
                      .filter((id) => id !== 0)}
                    onChange={(selectedIds) => {
                      const stringIds = Array.isArray(selectedIds)
                        ? selectedIds.map((id) => id.toString())
                        : [];
                      field.onChange(stringIds);
                    }}
                    multiple
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/groups")}
              >
                Cancelar
              </Button>
              <SubmitButton loading={loading} />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default VehicleGroupsForm;
